/**
 * HR Engine - Dialog de modification des objectifs d'une évaluation en cours
 * Disponible tant que l'évaluation manager n'a pas été soumise.
 */

import { useState, useEffect } from 'react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription
} from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Badge } from './ui/badge';
import { Plus, Trash2, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';

export default function EditObjectivesDialog({ open, onOpenChange, evaluation, onSaved }) {
  const [objectives, setObjectives] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open || !evaluation?.id) return;
    // Charge les derniers objectifs depuis le backend pour être à jour
    const load = async () => {
      try {
        const res = await api.get(`/performance-eval/evaluations/${evaluation.id}`);
        const objs = res.data?.objectives || evaluation.objectives || [];
        setObjectives(
          objs.map(o => ({
            id: o.id,
            title: o.title || '',
            description: o.description || '',
            indicators: o.indicators || '',
            weight: Number(o.weight || 20),
            hasScore: o.selfScore !== null && o.selfScore !== undefined,
          }))
        );
      } catch (e) {
        // Fallback sur ce qu'on a déjà
        setObjectives(
          (evaluation.objectives || []).map(o => ({
            id: o.id,
            title: o.title || '',
            description: o.description || '',
            indicators: o.indicators || '',
            weight: Number(o.weight || 20),
            hasScore: o.selfScore !== null && o.selfScore !== undefined,
          }))
        );
      }
    };
    load();
  }, [open, evaluation]);

  const totalWeight = objectives.reduce((sum, o) => sum + (Number(o.weight) || 0), 0);

  const updateObjective = (idx, field, value) => {
    setObjectives(prev => {
      const next = [...prev];
      next[idx] = { ...next[idx], [field]: value };
      return next;
    });
  };

  const addObjective = () => {
    const remaining = Math.max(0, 100 - totalWeight);
    setObjectives(prev => [
      ...prev,
      { title: '', description: '', indicators: '', weight: remaining || 20, hasScore: false },
    ]);
  };

  const removeObjective = (idx) => {
    setObjectives(prev => prev.filter((_, i) => i !== idx));
  };

  const handleSave = async () => {
    // Validation
    if (objectives.length === 0) {
      toast.error('Au moins un objectif est requis');
      return;
    }
    const empty = objectives.find(o => !o.title.trim());
    if (empty) {
      toast.error('Tous les objectifs doivent avoir un titre');
      return;
    }
    if (totalWeight !== 100) {
      toast.error(`La somme des pondérations doit être égale à 100 (actuel : ${totalWeight})`);
      return;
    }

    setSaving(true);
    try {
      const payload = {
        objectives: objectives.map(o => ({
          id: o.id,
          title: o.title.trim(),
          description: o.description || '',
          indicators: o.indicators || '',
          weight: Number(o.weight),
        })),
      };
      const res = await api.put(
        `/performance-eval/evaluations/${evaluation.id}/objectives`,
        payload
      );
      toast.success(res.data?.message || 'Objectifs mis à jour');
      onSaved?.(res.data?.objectives);
      onOpenChange(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur lors de la mise à jour');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="edit-objectives-dialog">
        <DialogHeader>
          <DialogTitle>Modifier les objectifs de l'évaluation</DialogTitle>
          <DialogDescription>
            Ajoutez, modifiez ou supprimez les objectifs. Les scores déjà saisis en
            auto-évaluation pour les objectifs conservés sont préservés.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Indicateur de pondération total */}
          <div className={`flex items-center justify-between p-3 rounded-lg border ${
            totalWeight === 100
              ? 'bg-emerald-50 border-emerald-200 text-emerald-700'
              : 'bg-amber-50 border-amber-200 text-amber-700'
          }`}>
            <span className="font-medium">Total pondérations :</span>
            <span className="font-bold">{totalWeight}% {totalWeight === 100 ? '✓' : '/ 100 requis'}</span>
          </div>

          {objectives.map((o, idx) => (
            <div
              key={o.id || `new-${idx}`}
              className="border rounded-lg p-4 space-y-3 relative bg-white"
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Badge variant="outline">Objectif {idx + 1}</Badge>
                  {o.hasScore && (
                    <Badge className="bg-blue-100 text-blue-700 text-xs">Auto-éval déjà saisie</Badge>
                  )}
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => removeObjective(idx)}
                  className="text-red-600 hover:text-red-700 hover:bg-red-50"
                  data-testid={`remove-objective-${idx}`}
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>

              <div>
                <Label>Titre *</Label>
                <Input
                  value={o.title}
                  onChange={(e) => updateObjective(idx, 'title', e.target.value)}
                  placeholder="Ex: Augmenter le CA de 15%"
                  data-testid={`objective-title-${idx}`}
                />
              </div>

              <div>
                <Label>Description</Label>
                <Textarea
                  value={o.description}
                  onChange={(e) => updateObjective(idx, 'description', e.target.value)}
                  rows={2}
                  placeholder="Contexte et détails de l'objectif"
                />
              </div>

              <div>
                <Label>Indicateurs de mesure</Label>
                <Input
                  value={o.indicators}
                  onChange={(e) => updateObjective(idx, 'indicators', e.target.value)}
                  placeholder="Ex: CA mensuel, nombre de signatures"
                />
              </div>

              <div>
                <Label>Pondération (%)</Label>
                <Input
                  type="number"
                  min="1"
                  max="100"
                  value={o.weight}
                  onChange={(e) => updateObjective(idx, 'weight', parseInt(e.target.value, 10) || 0)}
                  className="w-32"
                  data-testid={`objective-weight-${idx}`}
                />
              </div>

              {o.hasScore && (
                <div className="flex items-start gap-2 text-xs text-blue-700 bg-blue-50 p-2 rounded">
                  <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                  <span>
                    Cet objectif a déjà été évalué par l'employé. Si vous le conservez,
                    son score sera préservé. Si vous modifiez le titre/indicateurs,
                    pensez à alerter l'employé pour qu'il réévalue.
                  </span>
                </div>
              )}
            </div>
          ))}

          <Button
            variant="outline"
            onClick={addObjective}
            className="w-full"
            data-testid="add-objective-btn"
          >
            <Plus className="w-4 h-4 mr-2" /> Ajouter un objectif
          </Button>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Annuler</Button>
          <Button
            onClick={handleSave}
            disabled={saving || totalWeight !== 100 || objectives.length === 0}
            data-testid="save-objectives-btn"
          >
            {saving ? 'Enregistrement...' : 'Enregistrer les objectifs'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
